import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { toast } from "sonner";

const HERO_IMG = "https://images.pexels.com/photos/9099794/pexels-photo-9099794.jpeg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back, rep.");
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "EMAIL_NOT_VERIFIED") {
        toast.message("Verify your email to continue — we sent you a fresh code.");
        navigate("/verify", { state: { email: email.toLowerCase() } });
        return;
      }
      setError(formatApiErrorDetail(detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#1A1A1A] text-white">
      <div className="relative hidden lg:block overflow-hidden border-r border-white/10">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/30 to-transparent" />
        <div className="absolute bottom-10 left-10">
          <div className="font-display text-7xl leading-[0.85] tracking-tight">
            CLAIM
            <br />
            YOUR <span className="text-[hsl(var(--primary))]">EVENT.</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 grid-lines">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="flex items-center gap-2.5 mb-10">
            <img src="/rtb-logo.png?v=2" alt="Right To Bear" className="h-16 w-auto object-contain" />
            <span className="flex flex-col leading-none">
              <span className="font-display text-2xl tracking-tight">RIGHT TO BEAR</span>
              <span className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--rtb-copper))] font-medium mt-1">
                Pure Peace of Mind
              </span>
            </span>
          </Link>

          <div className="overline text-[hsl(var(--primary))] mb-3">Rep Access</div>
          <h1 className="font-display text-5xl tracking-tight leading-none mb-8">LOG IN</h1>

          {error && (
            <div data-testid="login-error" className="border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-sm px-4 py-3 mb-5">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="overline text-white/50 block mb-2">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#22303C] border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                placeholder="rep@righttobear.com"
              />
            </div>
            <div>
              <label className="overline text-white/50 block mb-2">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#22303C] border border-white/15 px-4 py-3 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  data-testid="toggle-login-password"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-[hsl(var(--primary))] text-white overline py-4 hard-shadow-hover disabled:opacity-50"
            >
              {busy ? "Verifying…" : "Access Now"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <p className="text-white/40 text-sm mt-8">
            No account yet?{" "}
            <Link to="/register" data-testid="to-register" className="text-white underline hover:text-[hsl(var(--primary))]">
              Join the team
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
